import { Copy } from "lucide-react";

import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { formatAccountGroups } from "../services/depositAccounts.service";
import type { DepositAccountRow, DepositAccountsCorridor } from "../types/depositAccounts.types";

export interface DepositAccountDetailDrawerProps {
  corridor: DepositAccountsCorridor;
  account: DepositAccountRow;
  open: boolean;
  onClose: () => void;
}

function DetailRow({
  copyAriaLabel,
  label,
  value,
  copyValue,
  copySuccessMessage,
}: {
  copyAriaLabel: string;
  label: string;
  value: string;
  copyValue?: string;
  copySuccessMessage: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex min-w-0 items-center gap-1">
        <span className="truncate font-mono text-xs text-foreground" title={value}>
          {value}
        </span>
        {copyValue && (
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            aria-label={copyAriaLabel}
            onClick={() => {
              void navigator.clipboard
                .writeText(copyValue)
                .then(() => colocaToast.success(copySuccessMessage))
                .catch(() => undefined);
            }}
          />
        )}
      </span>
    </div>
  );
}

/**
 * Read-only detail of a deposit receiving-account mapping — there is no mutation endpoint, so no actions
 * here. `provider_reference` (dynamic CLABEs only) renders as an opaque reference, never labeled with the
 * provider's identity (rule 12). The flavor badge only renders when the row carries one (CLABEs).
 */
export function DepositAccountDetailDrawer({
  corridor,
  account,
  open,
  onClose,
}: DepositAccountDetailDrawerProps) {
  const { t } = useTranslations(corridor.i18nNamespace);

  const metadataEntries = Object.entries(account.metadata ?? {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="md"
      title={t("drawer_title")}
      description={t("page_subtitle")}
    >
      {/* Headline account */}
      <div className="mb-4 rounded-xl border border-border bg-muted/40 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-mono text-xl font-bold tracking-wide text-foreground tabular-nums">
            {formatAccountGroups(account.account, corridor.digitCount)}
          </p>
          <ColocaIconButton
            icon={Copy}
            variant="ghost"
            size="sm"
            aria-label={t("copy_aria")}
            onClick={() => {
              void navigator.clipboard
                .writeText(account.account)
                .then(() => colocaToast.success(t("copy_success")))
                .catch(() => undefined);
            }}
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {account.flavor && (
            <ColocaBadge variant="secondary" className="text-[10px]">
              {account.flavor === "dynamic" ? t("flavor_dynamic") : t("flavor_static")}
            </ColocaBadge>
          )}
          <ColocaBadge variant={account.is_active ? "success" : "default"} className="text-[10px]">
            {account.is_active ? t("status_active") : t("status_inactive")}
          </ColocaBadge>
        </div>
      </div>

      {/* Details */}
      <div className="mb-4 rounded-xl border border-border bg-card p-4">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("drawer_section_details")}
        </p>
        <div className="divide-y divide-border/60">
          <DetailRow
            label={t("drawer_id")}
            value={account.id}
            copyValue={account.id}
            copyAriaLabel={t("copy_aria")}
            copySuccessMessage={t("copy_success")}
          />
          {account.provider_reference && (
            <DetailRow
              label={t("drawer_reference")}
              value={account.provider_reference}
              copyAriaLabel={t("copy_aria")}
              copySuccessMessage={t("copy_success")}
            />
          )}
          <DetailRow
            label={t("drawer_created")}
            value={formatDateTime(account.created_at)}
            copyAriaLabel={t("copy_aria")}
            copySuccessMessage={t("copy_success")}
          />
          <DetailRow
            label={t("drawer_updated")}
            value={formatDateTime(account.updated_at)}
            copyAriaLabel={t("copy_aria")}
            copySuccessMessage={t("copy_success")}
          />
        </div>
      </div>

      {/* Metadata */}
      {metadataEntries.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("drawer_section_metadata")}
          </p>
          <div className="divide-y divide-border/60">
            {metadataEntries.map(([key, value]) => (
              <DetailRow
                key={key}
                label={key}
                value={typeof value === "string" ? value : JSON.stringify(value)}
                copyAriaLabel={t("copy_aria")}
                copySuccessMessage={t("copy_success")}
              />
            ))}
          </div>
        </div>
      )}
    </ColocaDrawer>
  );
}
